"use client";

import { useState, useRef, useEffect } from "react";
import { MapPin, Search, X, Building2, Home, Building, Store, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { toast } from "sonner";
import { clientAPI } from "@/lib/client-fetch";
import API_ENDPOINTS from "@/constants/api-endpoints";
import { useStoreLocation } from "@/context/store-location-context";

const libraries: ("places")[] = ["places"];

const MONTERREY_COORDS = { lat: 25.6866, lng: -100.3161 };

const CITY_COORDS: Record<string, { lat: number; lng: number }> = {
  monterrey: MONTERREY_COORDS,
  guadalajara: { lat: 20.6597, lng: -103.3496 },
  cdmx: { lat: 19.4326, lng: -99.1332 },
  saltillo: { lat: 25.42847, lng: -100.96797 },
};

type PlacePrediction = {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
};

type AddressDetails = {
  placeId: string;
  fullAddress: string;
  mainText: string;
  secondaryText: string;
  lat: number;
  lng: number;
};

type BuildingType = "Casa" | "Apto." | "Oficina" | "Negocio";

type FooterProps = {
  onBack: () => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  showBack: boolean;
};

type AddAddressFormProps = {
  onSuccess: (newAddressId: string | number) => void;
  onCancel: () => void;
  renderFooter?: (props: FooterProps) => React.ReactNode;
};

const BUILDING_TYPES: { id: BuildingType; label: string; icon: React.ReactNode }[] = [
  { id: "Casa", label: "Casa", icon: <Home className="h-5 w-5" /> },
  { id: "Apto.", label: "Apto.", icon: <Building2 className="h-5 w-5" /> },
  { id: "Oficina", label: "Oficina", icon: <Building className="h-5 w-5" /> },
  { id: "Negocio", label: "Negocio", icon: <Store className="h-5 w-5" /> },
];

export default function AddAddressForm({ onSuccess, onCancel, renderFooter }: AddAddressFormProps) {
  const { storeLocation } = useStoreLocation();
  const [step, setStep] = useState<"search" | "confirm">("search");
  const [searchQuery, setSearchQuery] = useState("");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<AddressDetails | null>(null);
  const [buildingType, setBuildingType] = useState<BuildingType | null>(null);
  const [streetNumber, setStreetNumber] = useState("");
  const [apartmentNumber, setApartmentNumber] = useState("");
  const [buildingName, setBuildingName] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{ title: string; message: string } | null>(null);

  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const userLocation = useRef<{ lat: number; lng: number } | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
    libraries,
  });

  useEffect(() => {
    if (isLoaded && !autocompleteService.current) {
      autocompleteService.current = new google.maps.places.AutocompleteService();
    }
  }, [isLoaded]);

  // Resolve location bias: GPS > storeLocation city > IP > Monterrey
  useEffect(() => {
    // 1. Set a synchronous fallback immediately so it's ready before the user types
    const cityKey = storeLocation.city?.toLowerCase();
    const cityCoords = cityKey ? CITY_COORDS[cityKey] : null;
    userLocation.current = cityCoords ?? MONTERREY_COORDS;

    // 2. Try GPS — highest priority, overrides everything if granted
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          userLocation.current = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
        },
        async () => {
          // GPS denied — fall back to IP only if city coords weren't available
          if (!cityCoords) {
            try {
              const res = await fetch("/api/ip-location");
              const data = await res.json();
              if (data.lat && data.lng) {
                userLocation.current = { lat: data.lat, lng: data.lng };
              }
            } catch {
              // keep Monterrey default
            }
          }
        }
      );
    } else if (!cityCoords) {
      // No geolocation API at all — try IP
      fetch("/api/ip-location")
        .then((r) => r.json())
        .then((data) => {
          if (data.lat && data.lng) {
            userLocation.current = { lat: data.lat, lng: data.lng };
          }
        })
        .catch(() => {});
    }
  }, [storeLocation.city]);

  useEffect(() => {
    if (!searchQuery.trim() || !autocompleteService.current) {
      setPredictions([]);
      return;
    }

    const fetchPredictions = async () => {
      try {
        const request: google.maps.places.AutocompletionRequest = {
          input: searchQuery,
          componentRestrictions: { country: "mx" },
          types: ["address"],
        };
        if (userLocation.current) {
          request.location = new google.maps.LatLng(userLocation.current.lat, userLocation.current.lng);
          request.radius = 50000; // 50 km bias radius
        }
        const response = await autocompleteService.current!.getPlacePredictions(request);
        setPredictions(response?.predictions || []);
      } catch {
        setPredictions([]);
      }
    };

    const debounce = setTimeout(fetchPredictions, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSelectPrediction = (prediction: PlacePrediction) => {
    if (!isLoaded) return;
 
    // Create a temporary div for PlacesService
    const tempDiv = document.createElement("div");
    placesService.current = new google.maps.places.PlacesService(tempDiv);

    placesService.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ["geometry", "formatted_address", "address_components"],
      },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
          const components = place.address_components ?? [];
          const structuredFormatting = prediction.structured_formatting;

          // Extract number from main_text (e.g., "Magnolia 210" --> "210")
          const numberMatch = structuredFormatting.main_text.match(/\d+/);

          const hasRoute = components.some((c) => c.types.includes("route"));
          const hasStreetNumber = components.some((c) => c.types.includes("street_number")) || (!!numberMatch && !!numberMatch[0] && numberMatch[0].length > 0);

          if (!hasRoute || !hasStreetNumber) {
            setErrorDialog({
              title: "Dirección incompleta",
              message: "Por favor selecciona una dirección con calle y número. Evita seleccionar solo una colonia o fraccionamiento.",
            });
            return;
          }

          setSelectedAddress({
            placeId: prediction.place_id,
            fullAddress: place.formatted_address || prediction.description,
            mainText: structuredFormatting.main_text,
            secondaryText: structuredFormatting.secondary_text,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
          setStep("confirm");
          setStreetNumber(numberMatch ? numberMatch[0] : "");
        }
      }
    );
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setPredictions([]);
    searchInputRef.current?.focus();
  };

  const handleBackToSearch = () => {
    setStep("search");
    setSelectedAddress(null);
  };

  const handleSubmit = async () => {
    if (!selectedAddress) return;
    if (!buildingType) {
      setErrorDialog({ title: "Tipo de edificio", message: "Selecciona el tipo de edificio de tu domicilio." });
      return;
    }
    if (!streetNumber.trim()) {
      setErrorDialog({ title: "Número de calle", message: "Confirma el número de la calle de tu domicilio." });
      return;
    }
    setIsSubmitting(true);

    const googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${selectedAddress.placeId}`; // TODO

    const addressData = {
      fullAddress: selectedAddress.fullAddress,
      latitude: selectedAddress.lat,
      longitude: selectedAddress.lng,
      furtherInstructions: deliveryInstructions,
      googleMapsUrl: googleMapsUrl,
      apartmentNumber: apartmentNumber,
      streetNumber: streetNumber,
      buildingName: buildingName,
      buildingType: buildingType,
      should_validate_geofencing: true,
    };

    const result = await clientAPI.post<{ shipping_info: { id: string | number } }>(
      API_ENDPOINTS.USER_ADDRESSES,
      addressData
    );

    setIsSubmitting(false);

    if (result.ok && result.data?.shipping_info?.id) {
      toast.success("Dirección de entrega guardada exitosamente");
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      onSuccess(result.data!.shipping_info!.id);
    } else if (!result.ok) {
      const errorResult = result as { error_title?: string; error_message?: string };
      setErrorDialog({
        title: errorResult.error_title || "Error al guardar",
        message: errorResult.error_message || "No se pudo guardar la dirección. Intenta de nuevo.",
      });
    } else {
      setErrorDialog({
        title: "Error al guardar",
        message: "No se pudo guardar la dirección. Intenta de nuevo.",
      });
    }
  };

  if (loadError) {
    return (
      <div className="p-4 text-center text-red-500">
        Error al cargar Google Maps. Por favor intenta de nuevo.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Cargando...
      </div>
    );
  }

  // Step 1: Search
  if (step === "search") {
    return (
      <div className="flex flex-col flex-1 min-h-0 space-y-4 overflow-hidden pr-6">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            type="text"
            placeholder="Buscar dirección..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 focus-visible:ring-0 focus-visible:border-gray-300"
            autoFocus
          />
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Predictions list */}
        <div className="flex-1 min-h-0 space-y-1 overflow-y-auto overflow-x-hidden">
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelectPrediction(prediction)}
              className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-100 text-left cursor-pointer transition overflow-hidden"
            >
              <MapPin className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">
                  {prediction.structured_formatting.main_text}
                </p>
                <p className="text-sm text-muted-foreground truncate">
                  {prediction.structured_formatting.secondary_text}
                </p>
              </div>
            </button>
          ))}

          {searchQuery && predictions.length === 0 && (
            <p className="text-center text-muted-foreground py-4">
              No se encontraron resultados
            </p>
          )}
        </div>

        {/* Footer */}
        {renderFooter ? (
          renderFooter({
            onBack: () => {},
            onSubmit: () => {},
            onCancel,
            isSubmitting: false,
            showBack: false,
          })
        ) : (
          <div className="flex-shrink-0 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-4 border-t">
            <Button variant="outline" onClick={onCancel} className="cursor-pointer">
              Cancelar
            </Button>
          </div>
        )}

      {/* Error Dialog */}
      <Dialog open={!!errorDialog} onOpenChange={(open) => !open && setErrorDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{errorDialog?.title}</DialogTitle>
            <DialogDescription>{errorDialog?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorDialog(null)} className="bg-teal-400 hover:bg-teal-500 cursor-pointer">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    );
  }

  // Step 2: Confirm
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Scrollable content */}
      <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-4">
        {/* Map */}
        {selectedAddress && (
          <div className="rounded-lg overflow-hidden border h-[200px]">
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={{ lat: selectedAddress.lat, lng: selectedAddress.lng }}
              zoom={16}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
              }}
            >
              <Marker position={{ lat: selectedAddress.lat, lng: selectedAddress.lng }} />
            </GoogleMap>
          </div>
        )}

        {/* Selected address display */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-2">
            <MapPin className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm">{selectedAddress?.mainText}</p>
              <p className="text-xs text-muted-foreground">{selectedAddress?.secondaryText}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleBackToSearch}
            className="text-sm text-teal-600 hover:underline mt-2 cursor-pointer"
          >
            Cambiar dirección
          </button>
        </div>

        {/* Building type */}
        <div className="space-y-2">
          <Label>Tipo de edificio</Label>
          <div className="grid grid-cols-4 gap-2">
            {BUILDING_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => setBuildingType(type.id)}
                className={cn(
                  "flex flex-col items-center gap-1 p-3 rounded-lg border transition cursor-pointer",
                  buildingType === type.id
                    ? "border-teal-400 bg-teal-50 text-teal-700"
                    : "hover:border-gray-300"
                )}
              >
                {type.icon}
                <span className="text-xs">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Street number */}
        <div className="space-y-2">
          <Label htmlFor="streetNumber">Confirma número de calle</Label>
          <Input
            id="streetNumber"
            type="text"
            placeholder="Ej: 123"
            value={streetNumber}
            className="focus-visible:ring-0 focus-visible:border-gray-300"
            onChange={(e) => setStreetNumber(e.target.value)}
          />
        </div>

        {/* Apartment number */}
        <div className="space-y-2">
          <Label htmlFor="apartmentNumber">
            Piso, oficina o apto. (opcional)
          </Label>
          <Input
            id="apartmentNumber"
            type="text"
            placeholder="Ej: 4B, Piso 3"
            value={apartmentNumber}
            className="focus-visible:ring-0 focus-visible:border-gray-300"
            onChange={(e) => setApartmentNumber(e.target.value)}
          />
        </div>

        {/* Building name - optional */}
        <div className="space-y-2">
          <Label htmlFor="buildingName">Nombre del edificio o negocio (opcional)</Label>
          <Input
            id="buildingName"
            type="text"
            placeholder="Ej: Torre Reforma, Edificio Central"
            value={buildingName}
            onChange={(e) => setBuildingName(e.target.value)}
            className="focus-visible:ring-0 focus-visible:border-gray-300"
          />
        </div>

        {/* Delivery instructions */}
        <div className="space-y-2">
          <Label htmlFor="deliveryInstructions">Instrucciones de entrega (opcional)</Label>
          <Textarea
            id="deliveryInstructions" 
            className="focus-visible:ring-0 focus-visible:border-gray-300"
            placeholder="Ej: Portón negro, tocar timbre 2 veces"
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
            rows={3}
          />
        </div>
        
        <div className="mb-10"></div>
      </div>

      {/* Footer */}
      {renderFooter ? (
        renderFooter({
          onBack: handleBackToSearch,
          onSubmit: handleSubmit,
          onCancel,
          isSubmitting,
          showBack: true,
        })
      ) : (
        <div className="flex-shrink-0 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-4 border-t bg-white">
          <Button variant="outline" onClick={handleBackToSearch} className="cursor-pointer" disabled={isSubmitting}>
            Atrás
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-teal-400 hover:bg-teal-500 cursor-pointer"
          >
            {isSubmitting ? "Guardando..." : "Guardar dirección"}
          </Button>
        </div>
      )}

      {/* Error Dialog */}
      <Dialog open={!!errorDialog} onOpenChange={(open) => !open && setErrorDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{errorDialog?.title}</DialogTitle>
            <DialogDescription>{errorDialog?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorDialog(null)} className="bg-teal-400 hover:bg-teal-500 cursor-pointer">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
